// 📊 Vue d'ensemble V1 — dialogue « CA sécurisé » : mois × département, sélecteur, recherche client/installateur, composition LMDS.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Search } from 'lucide-react';
import { formatCurrency } from '@/lib/config';
import { API_URL } from '@/lib/apiUrl';

const DEPTS = ['PRQC', 'LMDS', 'LOCATION'];

export function CASecuriseDialog({ annee, onFermer, deptInitial = 'global' }) {
  const [q, setQ] = useState('');
  const [dept, setDept] = useState(deptInitial === 'global' ? '' : deptInitial);
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/finance/ca-securise/detail`, { params: { annee, departement: dept, q } })
        .then((r) => { setData(r.data); setErreur(''); }).catch(() => setErreur('Impossible de charger le détail du CA sécurisé.'));
    }, 250);
    return () => clearTimeout(t);
  }, [annee, dept, q]);
  const mois = Object.entries(data?.par_mois || {}).sort(([a], [b]) => a.localeCompare(b));
  const comp = data?.lmds?.composition;
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onFermer} data-testid="dialog-ca-securise">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-4 space-y-3 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-bold text-lg">CA sécurisé {annee}</h2>
              <p className="text-xs text-slate-500">PRQC / LOCATION : contrats signés au mois du 1er bon de travail. LMDS : CA brut des semaines de rapports (contrats + appels de service + km, ajusté), sans les factures impayées.</p>
            </div>
            <button onClick={onFermer} className="p-1 hover:bg-slate-100 rounded" data-testid="ca-securise-fermer"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client (dossiers) ou installateur (LMDS)…" className="w-full border rounded-lg pl-8 pr-2 py-2 text-sm" data-testid="ca-securise-recherche" />
            </div>
            <select value={dept} onChange={(e) => setDept(e.target.value)} className="border rounded-lg px-2 py-2 text-sm" data-testid="ca-securise-departement">
              <option value="">Tous les départements</option>
              {DEPTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <span className="text-sm font-bold ml-auto" data-testid="ca-securise-total">Total : {formatCurrency(data?.total || 0)}</span>
            {data?.filtre_actif && <span className="text-sm font-semibold text-indigo-700 w-full text-right" data-testid="ca-securise-total-selection">Total de la sélection : {formatCurrency(data.total_filtre || 0)} ({data.entrees.length} entrée(s))</span>}
          </div>
        </div>
        <div className="p-4 text-sm space-y-4">
          {erreur && <p className="text-red-600" data-testid="ca-securise-erreur">{erreur}</p>}
          {data && (
            <table className="w-full" data-testid="ca-securise-table">
              <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-1">Mois de production</th>{DEPTS.map((d) => <th key={d} className="text-right">{d}</th>)}<th className="text-right">Total</th>{data.filtre_actif && <th className="text-right text-indigo-700">Sélection</th>}</tr></thead>
              <tbody>
                {mois.map(([cle, m]) => (
                  <tr key={cle} className="border-b" data-testid={`ca-securise-mois-${cle}`}>
                    <td className="py-1.5 font-medium">{m.label}</td>{DEPTS.map((d) => <td key={d} className="text-right">{formatCurrency(m[d] || 0)}</td>)}<td className="text-right font-bold">{formatCurrency(m.total ?? m.montant)}</td>
                    {data.filtre_actif && <td className="text-right text-indigo-700" data-testid={`ca-securise-mois-${cle}-selection`}>{formatCurrency(data.par_mois_filtre?.[cle]?.total || 0)}</td>}
                  </tr>
                ))}
                <tr className="font-bold bg-slate-50" data-testid="ca-securise-total-ligne">
                  <td className="py-2">Total général</td>{DEPTS.map((d) => <td key={d} className="text-right">{formatCurrency(data[d] || 0)}</td>)}<td className="text-right">{formatCurrency(data.total)}</td>
                </tr>
              </tbody>
            </table>
          )}
          {comp && (!dept || dept === 'LMDS') && (
            <div className="border rounded-lg p-3 bg-indigo-50/40" data-testid="ca-securise-composition-lmds">
              <p className="font-semibold mb-1">{data.lmds.libelle}</p>
              <ul className="text-xs grid grid-cols-2 gap-x-6 gap-y-1">
                <li className="flex justify-between"><span>Contrats installés</span><span>{formatCurrency(comp.contrats)}</span></li>
                <li className="flex justify-between"><span>Appels de service</span><span>{formatCurrency(comp.service_calls)}</span></li>
                <li className="flex justify-between"><span>Kilométrage</span><span>{formatCurrency(comp.km)}</span></li>
                <li className="flex justify-between"><span>Ajustements de factures</span><span>{formatCurrency(comp.ajustements)}</span></li>
              </ul>
              {data.lmds.ajustements?.map((a) => <p key={a.facture_id} className="text-xs text-rose-700 mt-1" data-testid={`ca-securise-ajustement-${a.facture_id}`}>{a.installateur_nom} — {a.libelle}{a.raison ? ` (${a.raison})` : ''}</p>)}
              <p className="text-xs font-bold mt-2">Total LMDS : {formatCurrency(data.LMDS)}</p>
            </div>
          )}
          {data && (
            <div data-testid="ca-securise-entrees">
              <p className="font-semibold mb-1">{data.entrees.length} entrée(s)</p>
              <ul className="divide-y text-xs max-h-72 overflow-y-auto border rounded-lg">
                {data.entrees.map((e) => (
                  <li key={e.id} className="flex justify-between gap-2 px-2 py-1.5" data-testid={`ca-securise-entree-${e.id}`}>
                    <span>
                      <span className={`inline-block w-16 text-[10px] font-bold ${e.departement === 'LMDS' ? 'text-indigo-700' : 'text-slate-600'}`}>{e.departement}</span>
                      {e.departement === 'LMDS' ? <a href={e.lien || '#'} className="text-blue-700 hover:underline">{e.client_nom}</a> : <a href={`/crm/dossiers/${e.id}`} className="text-blue-700 hover:underline">{e.client_nom}</a>}
                      <span className="text-slate-400 ml-2">{e.mois_label}</span>
                    </span>
                    <b className={e.montant < 0 ? 'text-rose-700' : ''}>{formatCurrency(e.montant)}</b>
                  </li>
                ))}
                {!data.entrees.length && <li className="px-2 py-3 text-center text-slate-500" data-testid="ca-securise-vide">Aucune entrée pour cette recherche.</li>}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
